import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Kanban from './pages/Kanban';
import QuotationsPage from './pages/Quotations';
import OrdersPage from './pages/Orders';
import Suppliers from './pages/Suppliers';
import Agents from './pages/Agents';
import Products from './pages/Products';
import Reports from './pages/Reports';
import Users from './pages/Users';
import Login from './pages/Login';
import AgentPortal from './pages/AgentPortal';
import Samples from './pages/Samples';
import Containers from './pages/Containers';
import ImportConfig from './pages/ImportConfig';
import { getUser, setSession, clearSession, isLoggedIn, getToken } from './utils/auth';
import { auth as authApi, isSessionLost, dropSession, SESSION_LOST_EVENT } from './utils/api';
import { I18nProvider } from './i18n/I18nContext';
import GlobalSearch from './components/GlobalSearch';
import ErrorBoundary from './components/ErrorBoundary';

// Fecha o drawer ao mudar de rota
function RouteWatcher({ onNavigate }) {
  const location = useLocation();
  useEffect(() => { onNavigate(); }, [location.pathname]);
  return null;
}

export default function App() {
  const [user,        setUser]        = useState(() => getUser());
  const [ready,       setReady]       = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Ao (re)abrir o sistema, o usuário guardado no localStorage é só um palpite:
  // o token pode ter vencido (validade de 7 dias) ou os dados estarem velhos.
  // Validamos com o servidor ANTES de renderizar: se a sessão não vale mais,
  // vai direto para o login — nunca para um dashboard com tudo zerado.
  useEffect(() => {
    let alive = true;
    if (!isLoggedIn()) { setReady(true); return; }
    setUser(getUser());
    authApi.me()
      .then(fresh => {
        if (!alive || !fresh) return;
        const u = fresh.user || fresh;
        if (u && u.id) { setSession(getToken(), u); setUser(u); }
      })
      .catch(err => {
        if (!alive) return;
        // Servidor recusou a sessão (token vencido/inválido) → login.
        // Falha de rede (sem resposta): segue com o usuário em cache para não
        // travar a tela de quem está só momentaneamente offline.
        if (isSessionLost(err)) { dropSession(); clearSession(); setUser(null); }
      })
      .finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, []);

  // Se a sessão cair a qualquer momento (token vencido no meio do uso), a tela
  // vai para o login na hora, em vez de continuar exibindo listas vazias.
  useEffect(() => {
    const onLost = () => setUser(null);
    window.addEventListener(SESSION_LOST_EVENT, onLost);
    return () => window.removeEventListener(SESSION_LOST_EVENT, onLost);
  }, []);

  function handleLogin(u)  { setUser(u); }
  function handleLogout()  { clearSession(); setUser(null); }
  function closeSidebar()  { setSidebarOpen(false); }

  if (!ready) return null;
  if (!user)  return <I18nProvider><Login onLogin={handleLogin} /></I18nProvider>;

  return (
    <ErrorBoundary>
    <I18nProvider>
    <BrowserRouter>
      <RouteWatcher onNavigate={closeSidebar} />
      <div className="app">

        {/* Overlay escuro atrás do drawer (só mobile) */}
        {sidebarOpen && (
          <div className="sidebar-overlay" onClick={closeSidebar} />
        )}

        <Sidebar user={user} onLogout={handleLogout} open={sidebarOpen} onClose={closeSidebar} />

        <main className="main">
          {/* Barra superior mobile: hamburger + logo */}
          <header className="mobile-header">
            <button className="hamburger" onClick={() => setSidebarOpen(o => !o)} aria-label="Menu">
              ☰
            </button>
            <img src="/logo-charth.png" alt="Charth" />
          </header>

          {/* Desktop search bar */}
          {user.role !== 'agent' && (
            <div style={{
              padding: '8px 24px',
              borderBottom: '1px solid var(--gray-100)',
              background: 'white',
              display: 'flex',
              alignItems: 'center',
            }}>
              <GlobalSearch />
            </div>
          )}

          <Routes>
            {/* Agent portal routes */}
            {user.role === 'agent' ? (
              <>
                <Route path="/agent-portal"  element={<AgentPortal mode="quotations" />} />
                <Route path="/agent-orders"  element={<AgentPortal mode="orders" />} />
                <Route path="*"              element={<Navigate to="/agent-portal" />} />
              </>
            ) : (
              <>
                <Route path="/"               element={<Dashboard />} />
                <Route path="/kanban"         element={<Kanban />} />
                <Route path="/quotations"     element={<QuotationsPage />} />
                <Route path="/quotations/:id" element={<QuotationsPage />} />
                <Route path="/orders"         element={<OrdersPage />} />
                <Route path="/orders/:id"     element={<OrdersPage />} />
                <Route path="/containers"     element={<Containers />} />
                <Route path="/containers/:id" element={<Containers />} />
                <Route path="/samples"        element={<Samples />} />
                <Route path="/suppliers"      element={<Suppliers />} />
                <Route path="/agents"         element={<Agents />} />
                <Route path="/products"       element={<Products />} />
                <Route path="/import-config"  element={<ImportConfig />} />
                <Route path="/reports"        element={<Reports />} />
                <Route path="/users"          element={
                  user.role === 'admin'
                    ? <Users currentUser={user} />
                    : <Navigate to="/" />
                } />
                <Route path="*" element={<Navigate to="/" />} />
              </>
            )}
          </Routes>
        </main>

      </div>
    </BrowserRouter>
    </I18nProvider>
    </ErrorBoundary>
  );
}
